import { useEffect, useLayoutEffect, useRef, useState } from "react";

export default function Collapse({
  expanded,
  mobileOnly = false,
  id,
  className = "",
  innerClassName = "",
  children,
}) {
  const panelRef = useRef(null);
  const contentRef = useRef(null);
  const measureRef = useRef(null);
  const observed = useRef(false);
  const heightRef = useRef(null);

  useLayoutEffect(() => {
    const content = contentRef.current;
    const measure = () => {
      // Round up so fractional borders aren't clipped by the viewport.
      const height = Math.ceil(
        Math.max(content.scrollHeight, content.getBoundingClientRect().height),
      );
      if (height !== heightRef.current) {
        heightRef.current = height;
        panelRef.current.style.setProperty("--collapse-height", `${height}px`);
      }
    };
    measureRef.current = measure;
    measure();
    if (typeof ResizeObserver !== "undefined") {
      observed.current = true;
      const observer = new ResizeObserver(measure);
      observer.observe(content);
      return () => observer.disconnect();
    }
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  // ResizeObserver handles content updates; only the fallback needs React changes.
  useLayoutEffect(() => {
    if (!observed.current) measureRef.current();
  }, [children]);

  const [mobile, setMobile] = useState(
    () =>
      typeof window.matchMedia === "function" &&
      window.matchMedia("(max-width: 576px)").matches,
  );
  useEffect(() => {
    if (!mobileOnly || typeof window.matchMedia !== "function") return;
    const query = window.matchMedia("(max-width: 576px)");
    const update = () => setMobile(query.matches);
    update();
    if (query.addEventListener) {
      query.addEventListener("change", update);
      return () => query.removeEventListener("change", update);
    }
    query.addListener(update);
    return () => query.removeListener(update);
  }, [mobileOnly]);
  const collapsed = !expanded && (!mobileOnly || mobile);
  const visible = !collapsed;
  return (
    <div
      ref={panelRef}
      id={id}
      className={`ui-collapse${mobileOnly ? " ui-collapse--mobile" : ""}${
        visible ? " is-open" : ""
      } ${className}`}
      aria-hidden={collapsed}
      inert={collapsed ? "" : undefined}
    >
      <div className="ui-collapse__viewport">
        <div
          className={`ui-collapse__content ${innerClassName}`}
          ref={contentRef}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
